import { getAuth } from "firebase/auth";

export type ModMedLocationView = {
  id: string;
  name: string;
  status: string;
  address: {
    line: string[];
    city: string;
    state: string;
    postalCode: string;
    country: string;
  };
};

export type ModMedAppointmentTypeView = {
  id: string;
  name: string;
};

export type ModMedPractitionerView = {
  id: string;
  name: string;
  active: boolean;
  telecom: Array<{ system: string; value: string; use?: string }>;
};

const apiBaseUrl = (): string => {
  const url = import.meta.env.VITE_API_URL;
  if (!url) throw new Error("VITE_API_URL is not configured");
  return url.replace(/\/$/, "");
};

const authHeader = async (): Promise<HeadersInit> => {
  const idToken = await getAuth().currentUser?.getIdToken();
  if (!idToken) throw new Error("Not authenticated");
  return { Authorization: `Bearer ${idToken}` };
};

const buildUrl = (path: string, queryParams?: Record<string, string>): string => {
  const url = new URL(`${apiBaseUrl()}${path}`);
  if (queryParams) {
    for (const [key, value] of Object.entries(queryParams)) {
      if (key && value) url.searchParams.append(key, value);
    }
  }
  return url.toString();
};

const request = async <T>(url: string): Promise<T> => {
  const response = await fetch(url, { headers: await authHeader() });
  if (!response.ok) {
    let message = `Request failed (${response.status})`;
    try {
      const body = (await response.json()) as { message?: string };
      if (body.message) message = body.message;
    } catch {
      console.error(message);
    }
    throw new Error(message);
  }
  return (await response.json()) as T;
};

export const fetchLocations = (
  customerId: string,
  queryParams?: Record<string, string>,
): Promise<ModMedLocationView[]> =>
  request(buildUrl(`/modmed/${customerId}/locations`, queryParams));

export const fetchAppointmentTypes = (customerId: string): Promise<ModMedAppointmentTypeView[]> =>
  request(buildUrl(`/modmed/${customerId}/appointment-types`));

export const fetchPractitioners = (
  customerId: string,
  queryParams?: Record<string, string>,
): Promise<ModMedPractitionerView[]> =>
  request(buildUrl(`/modmed/${customerId}/practitioners`, queryParams));
